import React, { createContext, useState, useContext, useReducer } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const initialState = {
  items: [],
  subtotal: 0,
  tax: 0,
  discount: 0,
  total: 0,
  customer: null,
  notes: ''
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      let newItems;
      if (existingItem) {
        newItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newItems = [...state.items, { ...action.payload, quantity: 1 }];
      }

      return calculateTotals({ ...state, items: newItems });
    }

    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      ).filter(item => item.quantity > 0);

      return calculateTotals({ ...state, items: newItems });
    }

    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return calculateTotals({ ...state, items: newItems });
    }

    case 'SET_DISCOUNT': {
      return calculateTotals({ ...state, discount: action.payload });
    }

    case 'SET_CUSTOMER': {
      return { ...state, customer: action.payload };
    }

    case 'SET_NOTES': {
      return { ...state, notes: action.payload };
    }

    case 'CLEAR_CART': {
      return initialState;
    }

    case 'LOAD_HELD_SALE': {
      return calculateTotals(action.payload);
    }

    default:
      return state;
  }
};

const calculateTotals = (state) => {
  const subtotal = state.items.reduce(
    (sum, item) => sum + (item.selling_price * item.quantity),
    0
  );

  const tax = state.items.reduce(
    (sum, item) => sum + ((item.selling_price * item.quantity * (item.tax_rate || 0)) / 100),
    0
  );

  const total = subtotal + tax - state.discount;

  return {
    ...state,
    subtotal,
    tax,
    total
  };
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_ITEM', payload: product });
    toast.success(`${product.product_name} added to cart`);
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
    toast.success('Item removed from cart');
  };

  const setDiscount = (amount) => {
    dispatch({ type: 'SET_DISCOUNT', payload: amount });
  };

  const setCustomer = (customer) => {
    dispatch({ type: 'SET_CUSTOMER', payload: customer });
  };

  const setNotes = (notes) => {
    dispatch({ type: 'SET_NOTES', payload: notes });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const loadHeldSale = (sale) => {
    dispatch({ type: 'LOAD_HELD_SALE', payload: sale });
  };

  const value = {
    ...state,
    addToCart,
    updateQuantity,
    removeFromCart,
    setDiscount,
    setCustomer,
    setNotes,
    clearCart,
    loadHeldSale
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};